<template>
    <div class="mt" :class="`mt--${fit}`">
        <!-- 找不同圖：把 attributes.targets 畫在圖上。
             ⚠⚠ **這個分支只能用 fit="free"**：熱區的百分比座標是相對於**圖片本身**，
             而 `object-fit: contain` / `cover` 會讓圖留白或裁切，於是「容器的百分比」
             不等於「圖的百分比」，每個框都偏移（800×600 放進 1.47:1 的框會偏 4.7%）。
             學生端 FindDifference.vue 用的就是 `w-full h-auto`。 -->
        <template v-if="isHotspotWithTargets">
            <img :src="media.url" class="mt-img" :alt="media.title">
            <span v-for="t in targets" :key="t.id" class="mt-target" :style="targetStyle(t)" />
        </template>

        <!-- 字表沒有檔案可看，所以列出單字本身。
             ⚠ **不捲動**：呼叫端可能把整塊包在一個點了就送出的 button 裡
             （挑選器就是），捲一下就變成誤選。超出的用「還有 N 個」表示。 -->
        <div v-else-if="isWordlistWithWords" class="mt-words">
            <span v-for="(w, i) in previewWords" :key="i" class="mt-word">{{ w }}</span>
            <span v-if="hiddenWordCount > 0" class="mt-word mt-word--more">
                {{ $t('unit.settings.word_more', { n: hiddenWordCount }) }}
            </span>
        </div>

        <!-- 縮圖圖檔：影片是上傳時 ffmpeg 抽的第 1 秒，PDF 是前端算繪的第 1 頁。
             兩者共用 attributes.poster_url。
             ⚠ **排在 image 與 embed 之前**：有縮圖就別下載整個 PDF，也別用原檔。
             ⚠ 不看 `url`——影片轉檔完成前它是空的，但縮圖已經有了。
             ⚠ **不要再疊播放圖示**（需求方 2026-09-23 移除）。縮圖是「這是什麼素材」的
             預覽，不是播放器；疊上去會讓人以為點下去會播，但這裡點下去是選取。
             影片的辨識交給 `kind` 欄位與沒有縮圖時的 `mdi-play-circle-outline` 圖示。 -->
        <template v-else-if="posterUrl">
            <img :src="posterUrl" class="mt-img" :alt="altText">
        </template>

        <img v-else-if="isImage" :src="media.url" class="mt-img" :alt="altText">

        <!-- 沒有縮圖的 PDF 退回內嵌算繪第一頁。
             ⚠⚠ **只有「一份素材」的欄位可以開這個**（allowEmbed）。`<embed>` 會下載
             **整個 PDF**（上限 20MB），所以清單或格線絕對不能用——一頁 24 筆就是
             最多 480MB 才畫出一頁縮圖。
             ⚠ 可行的前提是上傳時設了 `Content-Type: application/pdf` 且沒有設
             `Content-Disposition`（AwsS3Utils 兩者都符合），否則瀏覽器會變成下載。
             ⚠ `#toolbar=0&navpanes=0&scrollbar=0` 去掉檢視器介面，`view=FitH` 以寬度
             對齊——縮圖只看得到頁首，而那正好是學習單的標題。 -->
        <embed v-else-if="canEmbedPdf"
            :src="`${media.url}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`"
            type="application/pdf" class="mt-pdf">

        <!-- 什麼都畫不出來：圖示（＋可選的檔名） -->
        <div v-else class="mt-file">
            <v-icon :size="iconSize" :color="iconColor">{{ iconName }}</v-icon>
            <span v-if="showFileName && media" class="mt-file-name">{{ media.title }}</span>
        </div>
    </div>
</template>

<script>
import { attrsOf, wordsOf } from './mediaConstants';

/**
 * 素材的縮圖 — 一個地方決定「一份素材長什麼樣子」。
 *
 * ⚠ **抽出來的理由**：這段邏輯原本在 `MediaSlot` / `MediaPicker` / `MediaLibrary`
 * 各有一份，而它們已經開始不同步——PDF 的 `poster_url` 加上去時只改了兩處，
 * 影片的播放圖示條件也曾經只在其中一份寫對。每加一種 purpose 就要記得改三個地方。
 *
 * ⚠ **這個元件只負責「畫什麼」，不負責「多大」。** 尺寸、邊框、圓角、背景都由呼叫端
 * 的容器決定（各處的框差很多：88×66、104×78、格線的 68% padding-top）。
 * 呼叫端的容器需要 `overflow: hidden` 與（`free` 以外）確定的高度。
 *
 * ⚠⚠ **「確定的高度」不包含用 `padding-top` 百分比撐出比例的容器。** 那種容器的
 * content 高度是 auto（高度來自 padding），而 CSS 規定容器高度依內容決定時，子元素的
 * 百分比高度視為 auto——於是這個元件的 `height: 100%` 解析不出來，圖片會以原始比例排在
 * padding 底下，格子被撐得很高、上半部一片空白。
 * **那種容器必須把這個元件絕對定位**（`position: absolute; top/left/right/bottom: 0`），
 * 見 `MediaPicker` 的 `.mp-fill`。2026-09-08 就是漏掉這一點，整個挑選器的縮圖都壞掉。
 *
 * ⚠ **每個 prop 的預設值都是「最保守的既有行為」**，所以加上這個元件不會改變任何
 * 呼叫端的外觀——要新行為的地方自己打開。
 */
export default {
    name: 'MediaThumb',
    props: {
        /** 素材（清單列、`QuestMediaBrief`、上傳回傳都可以）。null = 什麼都不畫。 */
        media: { type: Object, default: null },
        /**
         * 圖片怎麼填滿容器。
         * - `cover`（預設）：裁切填滿。小縮圖比較整齊——素材欄與「我的素材」頁用它。
         * - `contain`：完整顯示、可能留白。挑選器用它（老師要看到整張圖才選得準）。
         * - `free`：**圖片自己決定高度**（`width:100%; height:auto`）。
         *   ⚠ 熱區與字表**必須**用這個，見 template 裡的說明。
         */
        fit: {
            type: String,
            default: 'cover',
            validator: (v) => ['cover', 'contain', 'free'].indexOf(v) !== -1,
        },
        /**
         * 找不同圖要不要把熱區框畫出來。
         * ⚠ 打開時 `fit` 必須是 `free`，否則框會整體偏移。
         */
        showTargets: { type: Boolean, default: false },
        /** 字表要不要列出單字（否則退回圖示）。⚠ 打開時 `fit` 建議 `free`。 */
        showWords: { type: Boolean, default: false },
        /** 最多列幾個字，超出的用「還有 N 個」。⚠ 這是呈現上限，不是資料上限（60）。 */
        maxWords: { type: Number, default: 8 },
        /**
         * 沒有 `poster_url` 的 PDF 要不要用 `<embed>` 內嵌算繪第一頁。
         * ⚠⚠ **只有「一份素材」的欄位可以開**：它會下載整個 PDF。
         */
        allowEmbed: { type: Boolean, default: false },
        /** 退回圖示時要不要同時顯示檔名（挑選器的格線用）。 */
        showFileName: { type: Boolean, default: false },
        iconSize: { type: [Number, String], default: 28 },
    },
    computed: {
        attrs() {
            /// ⚠ 走 `attrsOf` 才擋得住 MariaDB 的 JSON 回字串（規格 §2.1）。
            /// 直接當物件用會靜默拿到 undefined——熱區不畫、字表是空的，而且沒有錯誤訊息。
            return attrsOf(this.media);
        },
        altText() {
            return (this.media && this.media.title) || '';
        },
        isImage() {
            return !!(this.media && this.media.kind === 'image' && this.media.url);
        },
        posterUrl() {
            /// 影片與 PDF 共用同一個 key，所以這裡不分 kind——多一種有縮圖的素材時
            /// 這個元件不用改。
            return this.media ? (this.attrs.poster_url || '') : '';
        },
        canEmbedPdf() {
            return this.allowEmbed && !!(this.media && this.media.kind === 'pdf' && this.media.url);
        },
        targets() {
            const t = this.attrs.targets;
            return Array.isArray(t) ? t : [];
        },
        isHotspotWithTargets() {
            /// ⚠ 用 `purpose` 而不是 `kind`：`kind` 是 image，跟封面圖與翻卡牌用圖
            /// 分不開（那正是 `purpose` 存在的理由，ADR 0004）。
            return this.showTargets
                && !!(this.media && this.media.purpose === 'hotspot' && this.media.url);
        },
        allWords() {
            return wordsOf(this.media);
        },
        isWordlistWithWords() {
            /// ⚠ 用 `kind` 而不是 `purpose`（跟熱區相反）：字表目前只有 wordsearch /
            /// colorword 兩個 purpose，但**未來會有更多**，用 kind 判斷的話不認識的
            /// 新字表 purpose 也還是列得出單字（規格 §2.5 的容忍規則）。
            return this.showWords && !!(this.media && this.media.kind === 'wordlist');
        },
        previewWords() {
            return this.allWords.slice(0, this.maxWords);
        },
        hiddenWordCount() {
            return Math.max(0, this.allWords.length - this.maxWords);
        },
        iconName() {
            if (!this.media) {
                return 'mdi-image-plus-outline';
            }
            switch (this.media.kind) {
                case 'pdf': return 'mdi-file-pdf-box';
                case 'video': return 'mdi-play-circle-outline';
                case 'wordlist': return 'mdi-format-list-bulleted';
                /// 互動影片問答的語音（系統生成，老師不能上傳）
                case 'audio': return 'mdi-volume-high';
                default: return 'mdi-image-outline';
            }
        },
        iconColor() {
            if (!this.media) {
                return '#cfd8dc';
            }
            switch (this.media.kind) {
                case 'pdf': return '#e57373';
                case 'video': return '#5c6bc0';
                case 'audio': return '#26a69a';
                default: return '#90a4ae';
            }
        },
    },
    methods: {
        /**
         * 熱區框的位置。
         * ⚠ **座標系是素材的 `width` × `height`**（規格 §2.2）。公式與學生端的
         * `FindDifference.vue` 一致——那邊算錯一個位元，老師看到的框就跟學生點得到的
         * 位置對不上。
         * ⚠ `width` / `height` 是 NULL 的素材換算不出來（存題目時會被擋掉，但清單裡
         * 仍可能出現），那時直接不畫——畫出一堆位置錯誤的框比不畫更糟，老師看不出是壞的。
         */
        targetStyle(t) {
            const w = this.media && this.media.width;
            const h = this.media && this.media.height;
            if (!w || !h) {
                return { display: 'none' };
            }
            return {
                left: `${(t.pxX / w) * 100}%`,
                top: `${(t.pxY / h) * 100}%`,
                width: `${(t.pxW / w) * 100}%`,
                height: `${(t.pxH / h) * 100}%`,
            };
        },
    },
};
</script>

<style scoped>
/* ⚠ 這個元件**不設**尺寸、邊框、背景——那些由呼叫端的容器決定。
   它只負責填滿容器並畫出內容。 */
.mt {
    position: relative;
    width: 100%;
}

.mt--cover,
.mt--contain {
    height: 100%;
}

/* free：高度由圖片決定（熱區與字表必須用它，見 template 的說明） */
.mt--free {
    height: auto;
}

.mt-img {
    display: block;
    width: 100%;
}

.mt--cover .mt-img {
    height: 100%;
    object-fit: cover;
}

.mt--contain .mt-img {
    height: 100%;
    object-fit: contain;
}

.mt--free .mt-img {
    height: auto;
}

.mt-pdf {
    width: 100%;
    height: 100%;
    border: 0;
    /* ⚠ 不擋住外層的點擊，也避免老師在小框裡誤觸 PDF 檢視器 */
    pointer-events: none;
}

/* 熱區框：外框 + 半透明底兩者都要——純外框在深色圖上看不見。
   顏色與框選工具（.hp-rect）、唯讀預覽（.hv-target）一致。 */
.mt-target {
    position: absolute;
    border: 2px solid #fb923c;
    background: rgba(251, 146, 60, 0.22);
    border-radius: 2px;
}

.mt-words {
    display: flex;
    flex-wrap: wrap;
    align-content: flex-start;
    gap: 3px;
    padding: 5px;
}

.mt-word {
    font-size: 11px;
    line-height: 1;
    padding: 3px 5px;
    border-radius: 3px;
    background: #fff;
    border: 1px solid #cfd8dc;
    color: #37474f;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.mt-word--more {
    border-style: dashed;
    color: #90a4ae;
    background: #fafafa;
}


.mt-file {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 6px;
}

/* free 模式下沒有可以 inset 的高度，改成正常流 */
.mt--free .mt-file {
    position: static;
    padding: 16px 6px;
}

.mt-file-name {
    font-size: 11px;
    color: #607d8b;
    text-align: center;
    word-break: break-all;
    max-height: 30px;
    overflow: hidden;
}
</style>
